import { useEffect, useState } from 'react';

interface IUseMapPopupPositionArg {
  anchor: Element | null | undefined;
  popup: Element | null | undefined;
  container: Element | null | undefined;
  verticalOffset?: number;
  isVisible: boolean;
}

const getMapPopupBaseStyle = (hasPosition: boolean): React.CSSProperties => ({
  position: 'absolute',
  visibility: hasPosition ? 'visible' : 'hidden',
});

/**
 * This hook is responsible for the calculation of the placement of the map popup.
 *
 * Before we begin it's important to note that the placement calculation only happens at the
 * moment that the trigger to show the popup fires. This means that we won't try to reposition
 * the popup after the user interacts with the map.
 *
 * We need to have the refs for the map container, marker and popup in order to do that.
 * Having that, after we receive that `isVisible=true` we can begin the work of calculating
 * the appropriate position of the popup and this works in 2 steps.
 *
 * Step 1:
 *   Allow the popup to be displayed with `visibility: hidden` so we can get the ref for the
 *   popup element and then get the bounds of the popup.
 *
 * Step 2:
 *   After getting the bounds of all the elements we can calculate if the position of the marker
 *   relative to the container makes it possible to place the popup in its default[1] position,
 *   if not, we try to accomodate depending on the available space. Once we have the position
 *   calculated we can actually show the popup by using `visibility: visible`.
 *
 * The result of the calculation that makes the proper position possible is a `style` object
 * that must be applied to the popup element.
 *
 * And, in the end, when the popup is hidden we reset everything to its initial state to make
 * it easy for all the calculation process to happen when the popup must be shown again.
 *
 * [1] The default position of the popup is above of the marker and horizontally centered.
 */
export const useMapPopupPosition = ({
  anchor,
  popup,
  container,
  verticalOffset = 8,
  isVisible,
}: IUseMapPopupPositionArg): React.CSSProperties => {
  const [hasPosition, setHasPosition] = useState(false);
  const [style, setStyle] = useState<React.CSSProperties>(getMapPopupBaseStyle(hasPosition));

  useEffect(() => {
    if (!isVisible || !popup || !anchor || !container) {
      return;
    }

    const popupRect = popup.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const spaceTop = anchorRect.top - containerRect.top;
    const spaceLeft = anchorRect.right - containerRect.left;
    const spaceRight = containerRect.right - anchorRect.left;
    const remainingSpaceLeft = spaceLeft - popupRect.width;
    const remainingSpaceRight = spaceRight - popupRect.width;
    const popupHalfWidthCentered = (popupRect.width - anchorRect.width) / 2;

    const hasSpaceOnTop = spaceTop >= popupRect.height + verticalOffset;
    const hasSpaceOnX =
      popupHalfWidthCentered <= spaceLeft - anchorRect.width &&
      popupHalfWidthCentered <= spaceRight - anchorRect.width;

    setStyle({
      ...getMapPopupBaseStyle(true),
      [hasSpaceOnTop ? 'bottom' : 'top']: anchorRect.height + verticalOffset,
      left: hasSpaceOnX
        ? '50%'
        : remainingSpaceRight > remainingSpaceLeft
          ? containerRect.left - anchorRect.left
          : undefined,
      right:
        !hasSpaceOnX && remainingSpaceLeft > remainingSpaceRight
          ? anchorRect.right - containerRect.right
          : undefined,
      transform: hasSpaceOnX ? 'translateX(-50%)' : undefined,
    });
    setHasPosition(true);
  }, [isVisible, popup, anchor, container, verticalOffset]);

  useEffect(() => {
    if (!isVisible) {
      setStyle(getMapPopupBaseStyle(false));
      setHasPosition(false);
    }
  }, [isVisible]);

  return style;
};
